import { useEffect, useState } from "react";

// ui
import { Box, Card, Button, IconButton, Tooltip } from "@mui/material";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";

import { SystemConfigEditModal } from "../component/systemConfig/SystemConfigEditModal";
// Bar
import TableTopBar from "../component/TableTopBar";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// API
import { getSystemConfig, putSystemConfig } from "../api/systemConfig";

// OtherFunc
import { useWindowSize } from "../lib/useWindowSize";

// トークンマスク（V2-H1: 表示層のみ。コピーは生値維持）
const FULL_MASK_KEYS = new Set(["chatwork_api_token", "line_api_token"]);
const PARTIAL_MASK_KEYS = new Set(["chatwork_base_room_id"]);

function getDisplayValue(key: string, value: string | undefined): string {
  if (!value) return "";
  if (FULL_MASK_KEYS.has(key)) return "****************";
  if (PARTIAL_MASK_KEYS.has(key)) return value.slice(0, 4) + "*****";
  return value;
}

// プロフィール項目
const profileColumns = [
  {
    key: "password_master_account_screen",
    name: "マスターアカウント初期パスワード",
    admin: ["マスターアカウント"],
  },
  {
    key: "password_staff_account_screen",
    name: "スタッフアカウント初期パスワード",
    admin: ["マスターアカウント"],
  },
  {
    key: "chatwork_base_room_id",
    name: "ChatWork 社内通知用ルームID",
    admin: ["マスターアカウント"],
  },
  {
    key: "chatwork_api_token",
    name: "ChatWork APIトークン",
    admin: ["マスターアカウント"],
  },
  {
    key: "line_api_token",
    name: "LINE APIトークン",
    admin: ["マスターアカウント"],
  },
  {
    key: "contract_number_key",
    name: "自動付与申込番号　頭文字",
    admin: ["マスターアカウント"],
  },
];

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "1.5px 1.5px rgba(115, 115, 115, 0.8)",
    fontSize: 11,
  },
}));

function SystemList(props: {
  i: number;
  column: {
    key: string;
    name: string;
    admin: string[];
  };
  profileData:
    | {
        [key: string]: string;
      }
    | undefined;
  setEditSystemConfig: (
    data: {
      status: "before" | "editing" | "success" | "failed" | string;
      key: string;
      value: string;
      name: string;
    } | null
  ) => void;
}) {
  const { i, column, profileData, setEditSystemConfig } = props;
  const [openTooltip, setOpenTooltip] = useState(false);
  const displayValue = getDisplayValue(column.key, profileData?.[column.key]);
  return (
    <Box
      key={"profileBox_" + i}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        borderBottom: i < 5 ? "1px solid var(--color-border-light)" : "none",
      }}
    >
      <Box sx={{ flex: 1, fontSize: 13, color: "var(--color-sumi)", wordBreak: "break-all" }}>
        {column.name}
      </Box>
      <Box sx={{ fontSize: 12, color: "var(--color-sumi-muted)", wordBreak: "break-all", textAlign: "right" }}>
        {displayValue}
      </Box>
      <LightTooltip
        onClose={() => setOpenTooltip(false)}
        open={openTooltip}
        title="コピーしました"
      >
        <IconButton
          sx={{ width: 28, height: 28 }}
          aria-label="コピー"
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(
              profileData?.[column.key.replace("_screen", "")] as string
            );
            setOpenTooltip(true);
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </LightTooltip>
      <IconButton
        sx={{ width: 28, height: 28 }}
        aria-label="編集"
        size="small"
        onClick={() => {
          setEditSystemConfig({
            status: "before",
            key: column.key.replace("_screen", ""),
            name: column.name,
            value: "",
          });
        }}
      >
        <EditNoteIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}

function SystemConfig() {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const { windowWidth, windowHeight } = useWindowSize();

  // プロフィール
  const [profileData, setProfileData] = useState<{ [key: string]: string }>();
  async function getProfileData() {
    setIsLoading(true);
    const detail_data = await getSystemConfig();
    setProfileData(detail_data);
    setIsLoading(false);
  }
  useEffect(() => {
    getProfileData();
  }, []);

  const [editSystemConfig, setEditSystemConfig] = useState<{
    status: "before" | "editing" | "success" | "failed" | string;
    key: string;
    value: string;
    name: string;
  } | null>(null);

  async function putEditData() {
    if (editSystemConfig) {
      setIsLoading(true);
      setEditSystemConfig({
        ...editSystemConfig,
        status: "editing",
      });
      const res = await putSystemConfig(
        editSystemConfig.key,
        editSystemConfig.value
      );
      setEditSystemConfig({
        ...editSystemConfig,
        status: res,
      });
      getProfileData();
      setIsLoading(false);
    }
  }
  return (
    <Box
      data-testid="system-config-page"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <TableTopBar
        serch={false}
        serchOpen={false}
        setSerchOpen={() => {}}
        addDataNames={[]}
        addDataName={""}
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName={""}
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName={""}
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName={""}
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
      />
      <Box
        style={{
          marginTop: isSmartPhone ? "90px" : "10px",
          marginLeft: windowWidth < 800 ? "10px" : "20px",
          marginRight: windowWidth < 800 ? "10px" : "20px",
          maxWidth: 640,
        }}
      >
        <Card
          sx={{
            mb: 3,
            border: "1px solid var(--color-border)",
          }}
        >
          {profileColumns.map((column, i) => {
            const authority = loginUser ? loginUser?.authority : "";
            if (column.admin.includes(authority)) {
              return (
                <SystemList
                  key={"profileBox_" + i}
                  i={i}
                  column={column}
                  profileData={profileData}
                  setEditSystemConfig={setEditSystemConfig}
                />
              );
            }
          })}
        </Card>

      </Box>
      <SystemConfigEditModal
        onModal={
          editSystemConfig !== null && editSystemConfig.status !== "editing"
        }
        swCloseModal={() => setEditSystemConfig(null)}
        editSystemConfig={editSystemConfig}
        setEditSystemConfig={setEditSystemConfig}
        putEditData={putEditData}
      />
    </Box>
  );
}

export default SystemConfig;
